import { forwardRef, useCallback, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { ChevronDown } from "lucide-react";
import { usePhoneInput } from "@core/features/form";
import { DropdownBase, type DropdownOption } from "@core/ui";
import { CountryCode } from "libphonenumber-js";

export type Props = {
  /** Field name */
  name?: string;
  /** Input id */
  id?: string;
  /** Current E.164 phone number value */
  value?: string;
  /** Called when the E.164 value changes */
  onChange?: (value: string) => void;
  /** Called when input loses focus */
  onBlur?: () => void;
  /** Default country code (ISO 3166-1 alpha-2) */
  defaultCountry?: CountryCode;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** aria-describedby attribute */
  "aria-describedby"?: string;
  /** aria-labelledby attribute */
  "aria-labelledby"?: string;
  /** aria-required attribute */
  "aria-required"?: boolean | "true" | "false";
};

/**
 * International phone number input with integrated country code selector.
 * Stores value in E.164 format (e.g., "+15551234567").
 *
 * @example
 * <PhoneInput
 *   value="+15551234567"
 *   onChange={(value) => console.log(value)} // E.164 format
 *   placeholder="Enter phone number"
 * />
 */
export const PhoneInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      id,
      value = "",
      onChange,
      onBlur,
      defaultCountry,
      placeholder = "Enter phone number",
      error,
      disabled,
      className,
      "aria-describedby": ariaDescribedby,
      "aria-labelledby": ariaLabelledby,
      "aria-required": ariaRequired,
    },
    ref,
  ) => {
    const {
      countries,
      selectedCountry,
      displayValue,
      setCountry,
      setNationalNumber,
    } = usePhoneInput({
      value,
      defaultCountry,
      onChange,
    });

    const countryOptions: DropdownOption[] = useMemo(
      () => countries.map((c) => ({ value: c.code, label: c.name })),
      [countries],
    );

    const countryByCode = useMemo(
      () => new Map(countries.map((c) => [c.code, c])),
      [countries],
    );

    const handleCountryChange = useCallback(
      (code: string) => {
        const country = countryByCode.get(code as CountryCode);
        if (country) setCountry(country);
      },
      [countryByCode, setCountry],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setNationalNumber(e.target.value);
      },
      [setNationalNumber],
    );

    // When a label is already providing the accessible name, render a visual-only
    // placeholder to prevent screen readers from announcing both label and placeholder.
    const useVisualPlaceholder = !!(ariaLabelledby && placeholder);

    return (
      <div className={twMerge("relative flex", className)}>
        {/* Country selector */}
        <DropdownBase
          options={countryOptions}
          value={selectedCountry.code}
          onChange={handleCountryChange}
          disabled={disabled}
          aria-label="Select country"
          renderTrigger={({ isOpen, onClick, onKeyDown, listboxId }) => (
            <button
              type="button"
              onClick={onClick}
              onKeyDown={onKeyDown}
              disabled={disabled}
              className={twMerge(
                "flex items-center gap-1 px-3 py-2 border rounded-l-sm bg-brand-50",
                "hover:bg-brand-100 transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
                "disabled:cursor-not-allowed disabled:bg-brand-100 disabled:text-brand-300",
                error && "border-error-600 border-2 border-r-0",
                !error && "border-brand-800 border-r-0",
              )}
              aria-label={`Select country. Current: ${selectedCountry.name}`}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-controls={listboxId}
            >
              <span className="text-sm font-medium text-brand-700">
                {selectedCountry.code}
              </span>
              <span className="text-sm text-brand-700">
                +{selectedCountry.callingCode}
              </span>
              <ChevronDown
                className={twMerge(
                  "w-4 h-4 text-brand-500 transition-transform",
                  isOpen && "rotate-180",
                )}
              />
            </button>
          )}
          renderMenu={({ children, listboxProps }) => (
            <div
              {...listboxProps}
              className={twMerge(
                "absolute top-full left-0 mt-1 z-50",
                "w-72 max-h-64 overflow-y-auto",
                "bg-white border border-brand-200 rounded-sm shadow-lg",
              )}
            >
              {children}
            </div>
          )}
          renderItem={({
            option,
            isSelected,
            isHighlighted,
            onClick,
            id,
            tabIndex,
          }) => {
            const country = countryByCode.get(option.value as CountryCode);
            return (
              <div
                id={id}
                role="option"
                aria-selected={isSelected}
                aria-label={`${country?.name}, +${country?.callingCode}`}
                onClick={onClick}
                tabIndex={tabIndex}
                data-highlighted={isHighlighted}
                className={twMerge(
                  "w-full flex items-center gap-3 px-3 py-2 text-left cursor-pointer",
                  "hover:bg-brand-100 transition-colors",
                  "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-600",
                  isSelected && !isHighlighted && "bg-brand-100",
                  isHighlighted && "bg-brand-200",
                )}
              >
                <span className="text-sm font-medium text-brand-700 w-8">
                  {country?.code}
                </span>
                <span className="flex-1 text-sm truncate">{country?.name}</span>
                <span className="text-sm text-brand-500">
                  +{country?.callingCode}
                </span>
              </div>
            );
          }}
        />

        {/* Phone number input */}
        <div className="relative flex-1">
          <input
            ref={ref}
            type="tel"
            name={name}
            id={id}
            value={displayValue}
            onChange={handleInputChange}
            onBlur={onBlur}
            placeholder={useVisualPlaceholder ? " " : placeholder}
            disabled={disabled}
            aria-describedby={ariaDescribedby}
            aria-labelledby={ariaLabelledby}
            aria-required={ariaRequired}
            aria-invalid={error}
            autoComplete="tel-national"
            className={twMerge(
              "w-full border rounded-r-sm bg-transparent text-sm shadow-sm transition-colors px-3 py-2 min-w-0",
              "placeholder:text-brand-700",
              "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
              "disabled:cursor-not-allowed disabled:bg-brand-100 disabled:text-brand-300 disabled:border-brand-300",
              error && "border-error-600 border-2",
              !error && "border-brand-800",
              useVisualPlaceholder && "peer",
            )}
          />
          {useVisualPlaceholder && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-[5px] flex items-center h-[calc(1em+1rem)] text-sm text-brand-700 peer-[:not(:placeholder-shown)]:hidden peer-focus:hidden"
            >
              {placeholder}
            </span>
          )}
        </div>
      </div>
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
